import { useEffect, useState, useCallback } from "react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, Users, XCircle } from "lucide-react";

export default function HostBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/host/bookings");
      setBookings(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel this booking for ${b.guest_name}?`)) return;
    try {
      const { data } = await api.post(`/bookings/${b.id}/cancel`);
      toast.success(data.refund_note || "Booking cancelled");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    }
  };

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-display font-bold mb-2">Bookings</h1>
        <p className="text-[#717171] mb-8">Every reservation across your properties</p>

        {loading ? (
          <p className="text-[#717171]">Loading...</p>
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-12 text-center">
            <p className="text-[#717171]">No bookings yet. Once travelers book your rooms, you'll see them here.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden divide-y divide-[#EBEBEB]">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`host-booking-${b.id}`} className="p-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{b.hotel_name}</span>
                    <Badge className={b.status === "confirmed" ? "bg-[#008A05]/10 text-[#008A05] border-0" : b.status === "cancelled" ? "bg-[#C13515]/10 text-[#C13515] border-0" : "bg-[#FFB400]/10 text-[#A07000] border-0"}>{b.status}</Badge>
                  </div>
                  <p className="text-sm text-[#717171]">{b.room_name} · {b.guest_name} ({b.guest_email})</p>
                  <p className="text-xs text-[#717171] mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {b.check_in} → {b.check_out}</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {b.guests} guests · {b.nights}n</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-xl">{inr(b.total_price)}</p>
                  <p className="text-xs text-[#008A05]">100% to you · 0% commission</p>
                  {b.status !== "cancelled" && (
                    <Button data-testid={`host-cancel-booking-${b.id}`} variant="outline" className="rounded-xl h-8 text-xs mt-2 text-[#C13515] border-[#F3C0B3]" onClick={() => cancel(b)}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
